/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */

import MinimalDateRangePicker from '@brands/Components/MinimalDateRangePicker/MinimalDateRangePicker';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { calculateCaseScheduled, calculateProviderCaseDue } from '@brands/Utils/calculateCaseDue';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import { getStatusColorStyles } from '@brands/Utils/getColorForStatus';
import { getTextForStatus } from '@brands/Utils/getTextForStatus';
import { caseStatusOptions, caseTypeOptions, stateOptions } from '@brands/Utils/selectOptions';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { getClientDivisionOptions } from '@brands/services/identity/clientDivisionOptions';
import { ClientTag, UserRoleName } from '@brands/services/identity/types/UserProfile';
import {
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  ColumnVisibilityKey,
  setColumnVisibility as setPersistedColumnVisibility,
} from '@brands/store/slices/columnVisibilitySlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import { ICase, Status } from '@brands/services/cases/types/ICase';

import { Loading } from '../../../../../Components/LoadingSpinner/Loading';
import useCases from '../../../../../hooks/useCases';
import { usePersistedRowsPerPage } from '../../../../../hooks/usePersistedRowsPerPage';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/useReduxHook';
import useSubscribe, { Subscriber } from '../../../../../hooks/useSubscribe';
import styles from '../../styles.module.scss';
import PendingApproval from '../Components/PendingApproval';

const COLUMNS = [
  { key: 'scheduled_at', label: 'Scheduled date' },
  { key: 'due_date', label: 'Due date' },
  { key: 'case_type', label: 'Case type' },
  { key: 'patient', label: 'Patient' },
  { key: 'patient_current_state', label: 'Case state' },
  { key: 'client_tag', label: 'Client tags' },
  { key: 'client_division', label: 'Division' },
  { key: 'status', label: 'Status' },
  { key: 'id', label: 'Case ID' },
] as const;

type ColumnKey = (typeof COLUMNS)[number]['key'];
type CaseViewType = 'all' | 'completed';
type DateFilterKey = 'consultation_date' | 'due_date';

const CHAR_PX = 7.6;
const COL_PAD_PX = 48;
const COL_MIN_PX = 120;
const COL_MAX_PX = 400;

function columnWidthPx(label: string, cellStrings: string[]): number {
  const longest = [label, ...cellStrings].reduce((m, s) => Math.max(m, (s ?? '').length), 0);
  return Math.min(COL_MAX_PX, Math.max(COL_MIN_PX, Math.ceil(longest * CHAR_PX + COL_PAD_PX)));
}

function getClientTagStr(row: ICase): string {
  return row.client?.name || '-';
}

function getClientDivisionStr(row: ICase): string {
  return row.client_division?.name || '-';
}

function buildColumnWidths(
  rows: ICase[],
  cols: readonly { key: ColumnKey; label: string }[],
  roleName: string,
): Record<ColumnKey, number> {
  const out = {} as Record<ColumnKey, number>;
  const stringsFor = (key: ColumnKey): string[] =>
    rows.map((row) => {
      switch (key) {
        case 'scheduled_at':
          return calculateCaseScheduled(row.type, row.video_conference_date || '');
        case 'due_date':
          return calculateProviderCaseDue(row.type, row.started_at, row.video_conference_date || '', row.due_date);
        case 'case_type':
          return getFriendlyName(row.type);
        case 'id':
          return String(row.id);
        case 'patient_current_state':
          return row.patient_current_state ?? '-';
        case 'patient':
          return row.patient?.full_name ?? '-';
        case 'client_tag':
          return getClientTagStr(row);
        case 'client_division':
          return getClientDivisionStr(row);
        case 'status':
          return getTextForStatus(roleName, row.type, row.status);
        default:
          return '';
      }
    });
  cols.forEach((c) => {
    const vals = stringsFor(c.key);
    out[c.key] = columnWidthPx(c.label, vals.length ? vals : ['—']);
  });
  return out;
}

const headerCellSx = {
  backgroundColor: '#111827',
  color: '#FFFFFF',
  fontSize: 12,
  fontWeight: 600,
  padding: '8px 12px',
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  fontSize: 13,
  padding: '10px 12px',
};

const filterLabelSx = {
  fontSize: 12,
  fontWeight: 400,
};

const SORT_BY_MAP: Record<ColumnKey, string> = {
  scheduled_at: 'scheduled_at',
  due_date: 'due_date',
  case_type: 'type',
  id: 'case_id',
  patient_current_state: 'patient_current_state',
  patient: 'patient_full_name',
  client_tag: 'client_tag',
  client_division: 'client_division',
  status: 'status',
};

const CASE_VIEW_STATUS_MAP: Record<CaseViewType, string[]> = {
  all: [
    Status.Upcoming,
    'accepted_provider',
    'in_progress_provider',
    'in_progress_care_advisor',
    'completed_by_provider',
  ],
  completed: ['completed', 'canceled', 'in_progress_care_advisor', 'completed_by_provider'],
};

const SESSION_STATE_KEY = 'dentistry-one:provider:appointments';

const cleanFilters = (raw: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(raw).filter(([_, v]) => !(v == null || v === '' || (Array.isArray(v) && !v.length))),
  );

const isColumnKey = (value: string | null): value is ColumnKey => COLUMNS.some((column) => column.key === value);

const FilterCell = ({
  children,
  widthPx,
  isLast = false,
}: {
  children: React.ReactNode;
  widthPx: number;
  isLast?: boolean;
}) => (
  <TableCell
    sx={{
      background: '#FFF',
      padding: '4px 8px',
      borderRight: isLast ? 'none' : '1px solid #E5E7EB',
      ...(isLast ? { width: 'auto', minWidth: widthPx } : { width: widthPx, minWidth: widthPx, maxWidth: widthPx }),
      boxSizing: 'border-box',
    }}
  >
    {children}
  </TableCell>
);

const SortableHeader = ({
  label,
  column,
  sortBy,
  sortDirection,
  onSort,
  isLast,
  widthPx,
}: {
  label: string;
  column: ColumnKey;
  sortBy: ColumnKey | null;
  sortDirection: 'asc' | 'desc' | null;
  onSort: (c: ColumnKey) => void;
  isLast: boolean;
  widthPx: number;
}) => {
  const isActive = sortBy === column;
  const direction = isActive && sortDirection ? sortDirection : 'asc';

  return (
    <TableCell
      sx={{
        ...headerCellSx,
        borderRight: isLast ? 'none' : '1px solid #E5E7EB',
        ...(isLast ? { width: 'auto', minWidth: widthPx } : { width: widthPx, minWidth: widthPx, maxWidth: widthPx }),
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <TableSortLabel
        active={isActive}
        direction={direction}
        onClick={() => onSort(column)}
        sx={{
          maxWidth: '100%',
          width: '100%',
          color: 'var(--secondary_color) !important',
          '&.MuiTableSortLabel-root': { color: 'var(--secondary_color)' },
          '&.MuiTableSortLabel-root:hover': { color: 'var(--secondary_color)' },
          '& .MuiTableSortLabel-icon': { flexShrink: 0, color: 'var(--secondary_color) !important' },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            width: '100%',
          }}
        >
          {label}
        </Box>
      </TableSortLabel>
    </TableCell>
  );
};

const Appointments = (): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const location = useLocation();
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);
  const roleNavigate = useRoleNavigate();
  const { subscribe, unsubscribe } = useSubscribe();
  const timezone = moment.tz.guess();

  // "View full history" on the consultation page deep-links here with a
  // ?patient_full_name= query param. Seed the patient filter from it.
  const [searchParams] = useSearchParams();
  const initialPatientFilter = searchParams.get('patient_full_name')?.trim() ?? '';
  const incomingPatientPersistedRef = useRef(false);

  const [filtersReady, setFiltersReady] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>(() => {
    const { page, rowsPerPage: _rowsPerPage, sort_by, sort_direction, ...filters } = savedState;
    return initialPatientFilter ? { ...filters, patient_full_name: initialPatientFilter } : filters;
  });
  const [caseView] = useState<CaseViewType>('all');
  const [selectedRangeConsultationDate, setSelectedRangeConsultationDate] = useState<[Date, Date] | null>(null);
  const [selectedRangeDueDate, setSelectedRangeDueDate] = useState<[Date, Date] | null>(null);
  const [sortBy, setSortBy] = useState<ColumnKey | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  const [targetPageIndex, setTargetPageIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);

  useEffect(() => {
    listAllClientTags(UserRoleName.Provider)
      .then(({ nodes }) => setAllClientTags(nodes.filter((tag) => Boolean(tag.code))))
      .catch(() => setAllClientTags([]));
  }, []);

  const clientTagOptions = useMemo(
    () => allClientTags.map((tag) => ({ value: tag.code as string, label: tag.name })),
    [allClientTags],
  );

  const selectedClientTagIdsForDivision = useMemo(() => {
    const selectedCodes = (filterValues.client_tag as string[] | undefined) || [];
    return allClientTags.filter((tag) => selectedCodes.includes(tag.code || '')).map((tag) => tag.id);
  }, [allClientTags, filterValues.client_tag]);

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  const dispatch = useAppDispatch();
  const persistedColumnVisibility = useAppSelector(
    (s) => s.columnVisibility?.[ColumnVisibilityKey.ProviderAppointments],
  );
  const defaultColumnVisibility = useMemo(
    () => COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {} as Record<ColumnKey, boolean>),
    [],
  );
  const columnVisibility = useMemo(
    () => ({ ...defaultColumnVisibility, ...(persistedColumnVisibility ?? {}) } as Record<ColumnKey, boolean>),
    [persistedColumnVisibility, defaultColumnVisibility],
  );
  const setColumnVisibility = useCallback(
    (model: Record<ColumnKey, boolean>) => {
      dispatch(setPersistedColumnVisibility({ tableKey: ColumnVisibilityKey.ProviderAppointments, model }));
    },
    [dispatch],
  );
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.ProviderAppointments, 10);

  const [scrollbarThumbWidth, setScrollbarThumbWidth] = useState(0);
  const [scrollbarThumbLeft, setScrollbarThumbLeft] = useState(0);
  const [showCustomScrollbar, setShowCustomScrollbar] = useState(false);

  const visibleColumns = useMemo(() => COLUMNS.filter((c) => columnVisibility[c.key]), [columnVisibility]);

  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const customScrollbarRef = useRef<HTMLDivElement | null>(null);
  const isDraggingScrollbarRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);
  const rowsPerPageHydratedRef = useRef(false);

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const rowsPerPageFromSession = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    const pageFromSession = getSessionNumber(savedState, 'page', 1);
    const sortByFromSession = getSessionString(savedState, 'sort_by');
    const sortDirectionFromSession = getSessionString(savedState, 'sort_direction');

    setTargetPageIndex(initialPatientFilter ? 0 : pageFromSession > 0 ? pageFromSession - 1 : 0);
    setSortBy(isColumnKey(sortByFromSession) ? sortByFromSession : null);
    setSortDirection(
      sortDirectionFromSession === 'asc' || sortDirectionFromSession === 'desc' ? sortDirectionFromSession : null,
    );
    if (rowsPerPageFromSession > 0 && rowsPerPageFromSession !== rowsPerPage) {
      setRowsPerPage(rowsPerPageFromSession);
    }
    setFiltersReady(true);

    if (initialPatientFilter && !incomingPatientPersistedRef.current) {
      incomingPatientPersistedRef.current = true;
      writeSessionTableState(SESSION_STATE_KEY, {
        ...savedState,
        patient_full_name: initialPatientFilter,
        page: 1,
        rowsPerPage: rowsPerPageFromSession > 0 ? rowsPerPageFromSession : rowsPerPage,
      });
    }

    const hydrateRange = (key: DateFilterKey, setter: React.Dispatch<React.SetStateAction<[Date, Date] | null>>) => {
      const startRaw = savedState[`${key}_start`];
      const endRaw = savedState[`${key}_end`];

      const start = startRaw ? moment.utc(startRaw).tz(timezone).toDate() : null;
      const end = endRaw ? moment.utc(endRaw).tz(timezone).toDate() : null;

      if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        setter([start, end]);
      } else {
        setter(null);
      }
    };

    hydrateRange('consultation_date', setSelectedRangeConsultationDate);
    hydrateRange('due_date', setSelectedRangeDueDate);
  }, [initialPatientFilter, rowsPerPage, savedState, setRowsPerPage, timezone]);

  const commitFilters = useCallback(
    (raw: Record<string, any>) => {
      const cleaned = cleanFilters(raw);
      writeSessionTableState(SESSION_STATE_KEY, {
        ...cleaned,
        page: 1,
        rowsPerPage,
        ...(sortBy ? { sort_by: sortBy } : {}),
        ...(sortDirection ? { sort_direction: sortDirection } : {}),
      });

      setFilterValues(cleaned);
      setTargetPageIndex(0);
    },
    [rowsPerPage, sortBy, sortDirection],
  );

  const handleFilterChange = (key: string, value: any) => {
    const updated = { ...filterValues, [key]: value };
    commitFilters(updated);
  };

  const handleDateRangeFilterChange = (key: DateFilterKey, value: [Date, Date] | null): void => {
    const updated = { ...filterValues };

    if (key === 'consultation_date') {
      if (value && value[0] && value[1]) {
        updated.consultation_date_start = moment.tz(value[0], timezone).startOf('day').utc().toISOString();
        updated.consultation_date_end = moment.tz(value[1], timezone).endOf('day').utc().toISOString();
      } else {
        delete updated.consultation_date_start;
        delete updated.consultation_date_end;
      }

      setSelectedRangeConsultationDate(value);
    }

    if (key === 'due_date') {
      if (value && value[0] && value[1]) {
        updated.due_date_start = moment.tz(value[0], timezone).startOf('day').utc().toISOString();
        updated.due_date_end = moment.tz(value[1], timezone).endOf('day').utc().toISOString();
      } else {
        delete updated.due_date_start;
        delete updated.due_date_end;
      }

      setSelectedRangeDueDate(value);
    }

    commitFilters(updated);
  };

  const params = useMemo(() => {
    const { client_division_ids, ...restFilters } = filterValues;
    return {
      userRoleName: 'provider',
      provider_id: Number(userInfo.id),
      ...restFilters,
      ...(Array.isArray(client_division_ids) && client_division_ids.length > 0
        ? { client_division_ids: client_division_ids.map(Number) }
        : {}),
      limit: rowsPerPage,
      ...(filterValues.status ? {} : { status: CASE_VIEW_STATUS_MAP[caseView] }),
      ...(sortBy ? { sort_by: SORT_BY_MAP[sortBy] } : {}),
      ...(sortDirection ? { sort_direction: sortDirection } : {}),
    };
  }, [caseView, filterValues, rowsPerPage, sortBy, sortDirection, userInfo.id]);

  const { cases, loadNextPage, loadPreviousPage, reloadAll, dataLoading, totalNewCases, pageIndex } = useCases(
    params,
    filtersReady,
    userInfo.role.name,
  );

  useEffect(() => {
    if (!filtersReady || dataLoading || pageIndex >= targetPageIndex) return;
    void loadNextPage();
  }, [dataLoading, filtersReady, loadNextPage, pageIndex, targetPageIndex]);

  const columnWidths = useMemo(
    () => buildColumnWidths(cases, COLUMNS, userInfo.role.name),
    [cases, userInfo.role.name],
  );

  const lastVisibleKey = visibleColumns[visibleColumns.length - 1]?.key;

  const totalMinWidth = useMemo(
    () => visibleColumns.reduce((sum, c) => sum + columnWidths[c.key], 0),
    [visibleColumns, columnWidths],
  );

  useEffect(() => {
    const sub: Subscriber = {
      commands: ['case_updated', 'case_cancelled', 'case_new_message_created'],
      onMessage: (message: Record<string, any>) => {
        if (['case_updated', 'case_cancelled', 'case_new_message_created'].includes(message.command)) {
          reloadAll();
        }
      },
    };

    const id = subscribe(sub);
    return () => unsubscribe(id);
  }, [reloadAll, subscribe, unsubscribe]);

  const updateCustomScrollbar = useCallback(() => {
    const tableEl = tableScrollRef.current;
    const scrollbarEl = customScrollbarRef.current;

    if (!tableEl || !scrollbarEl) return;

    const { scrollWidth, clientWidth, scrollLeft } = tableEl;
    const trackWidth = scrollbarEl.clientWidth;

    if (trackWidth <= 0) return;

    const hasHorizontalOverflow = scrollWidth > clientWidth + 1;

    setShowCustomScrollbar(hasHorizontalOverflow);

    if (!hasHorizontalOverflow) {
      setScrollbarThumbWidth(trackWidth);
      setScrollbarThumbLeft(0);
      return;
    }

    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 40);
    const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
    const maxScrollLeft = Math.max(scrollWidth - clientWidth, 0);
    const thumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0;

    setScrollbarThumbWidth(thumbWidth);
    setScrollbarThumbLeft(thumbLeft);
  }, []);

  useEffect(() => {
    const tableEl = tableScrollRef.current;

    if (!tableEl) return;

    const handleScroll = () => {
      updateCustomScrollbar();
    };

    const handleResize = () => {
      updateCustomScrollbar();
    };

    updateCustomScrollbar();

    tableEl.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      tableEl.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [updateCustomScrollbar, cases.length, visibleColumns.length, rowsPerPage]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingScrollbarRef.current) return;

      const tableEl = tableScrollRef.current;
      const scrollbarEl = customScrollbarRef.current;

      if (!tableEl || !scrollbarEl) return;

      const maxScrollLeft = tableEl.scrollWidth - tableEl.clientWidth;
      if (maxScrollLeft <= 0) return;

      const trackWidth = scrollbarEl.clientWidth;
      const maxThumbLeft = Math.max(trackWidth - scrollbarThumbWidth, 0);
      const deltaX = event.clientX - dragStartXRef.current;

      const initialThumbLeft = maxScrollLeft > 0 ? (dragStartScrollLeftRef.current / maxScrollLeft) * maxThumbLeft : 0;

      const nextThumbLeft = Math.max(0, Math.min(maxThumbLeft, initialThumbLeft + deltaX));
      const nextScrollLeft = maxThumbLeft > 0 ? (nextThumbLeft / maxThumbLeft) * maxScrollLeft : 0;

      tableEl.scrollLeft = nextScrollLeft;
    };

    const handleMouseUp = () => {
      isDraggingScrollbarRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [scrollbarThumbWidth]);

  const handleScrollbarThumbMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    const tableEl = tableScrollRef.current;
    if (!tableEl) return;

    isDraggingScrollbarRef.current = true;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = tableEl.scrollLeft;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };

  const handleScrollbarTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const tableEl = tableScrollRef.current;
    const scrollbarEl = customScrollbarRef.current;

    if (!tableEl || !scrollbarEl) return;

    const rect = scrollbarEl.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const thumbCenterOffset = scrollbarThumbWidth / 2;
    const targetThumbLeft = Math.max(0, Math.min(rect.width - scrollbarThumbWidth, clickX - thumbCenterOffset));

    const maxThumbLeft = rect.width - scrollbarThumbWidth;
    const maxScrollLeft = tableEl.scrollWidth - tableEl.clientWidth;
    const nextScrollLeft = maxThumbLeft > 0 ? (targetThumbLeft / maxThumbLeft) * maxScrollLeft : 0;

    tableEl.scrollLeft = nextScrollLeft;
  };

  const handleSort = (column: ColumnKey) => {
    const nextSortDirection = sortBy === column ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
    if (sortBy !== column) {
      setSortBy(column);
      setSortDirection('asc');
    } else {
      setSortDirection(nextSortDirection);
    }
    setTargetPageIndex(0);
    writeSessionTableState(SESSION_STATE_KEY, {
      ...filterValues,
      page: 1,
      rowsPerPage,
      sort_by: column,
      sort_direction: nextSortDirection,
    });
  };

  const handleRowClick = (caseId: number): void => {
    sessionStorage.setItem('lastNonConsultationPagePath', `${location.pathname}${location.hash}`);
    roleNavigate(`/consultation-page/${caseId}`);
  };

  if (userInfo.status === 'Pending') {
    return <PendingApproval />;
  }

  if ((dataLoading && cases.length === 0) || !filtersReady) {
    return <Loading dotted />;
  }

  return (
    <div className={styles.providerDashboarTableDiv}>
      <div className={styles.providerDashboarTableHeader}>
        <div>
          {/* An h1 in the design, as the care advisor's identical Cases
              heading already is here. Same span-instead-of-heading as the
              collector's, which shares this class name. */}
          <h1 className={styles.providerDashboarTableHeaderText}>Cases</h1>
          <Box py={1} display="flex" justifyContent="flex-start">
            <Button
              startIcon={<ViewColumnIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              className={styles.manageColumnsBtn}
            >
              Manage columns
            </Button>
          </Box>
        </div>
      </div>

      <Box sx={{ width: '100%' }}>
        <Paper className={styles.providerTablePaper} sx={{ border: '1px solid #E5E7EB' }}>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box p={2}>
              {COLUMNS.map((c) => (
                <Box key={c.key} display="flex" alignItems="center">
                  <Checkbox
                    checked={columnVisibility[c.key]}
                    onChange={() =>
                      setColumnVisibility({
                        ...columnVisibility,
                        [c.key]: !columnVisibility[c.key],
                      })
                    }
                  />
                  {c.label}
                </Box>
              ))}
            </Box>
          </Popover>

          <TableContainer
            ref={tableScrollRef}
            className={styles.tableContainer}
            sx={{
              overflowX: 'auto',
              overflowY: 'auto',
            }}
          >
            <Table
              stickyHeader
              size="medium"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                minWidth: totalMinWidth,
              }}
            >
              <TableHead>
                <TableRow className={styles.tableHeaderRow}>
                  {visibleColumns.map((c, index) => (
                    <SortableHeader
                      key={c.key}
                      label={c.label}
                      column={c.key}
                      sortBy={sortBy}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      isLast={index === visibleColumns.length - 1}
                      widthPx={columnWidths[c.key]}
                    />
                  ))}
                </TableRow>

                <TableRow className={styles.tableFilterRow}>
                  {columnVisibility.scheduled_at && (
                    <FilterCell widthPx={columnWidths.scheduled_at} isLast={lastVisibleKey === 'scheduled_at'}>
                      <MinimalDateRangePicker
                        filterKey="consultation_date"
                        selectedRange={selectedRangeConsultationDate}
                        handleDateRangeFilterChange={handleDateRangeFilterChange}
                        width="100%"
                      />
                    </FilterCell>
                  )}

                  {columnVisibility.due_date && (
                    <FilterCell widthPx={columnWidths.due_date} isLast={lastVisibleKey === 'due_date'}>
                      <MinimalDateRangePicker
                        filterKey="due_date"
                        selectedRange={selectedRangeDueDate}
                        handleDateRangeFilterChange={handleDateRangeFilterChange}
                        width="100%"
                      />
                    </FilterCell>
                  )}

                  {columnVisibility.case_type && (
                    <FilterCell widthPx={columnWidths.case_type} isLast={lastVisibleKey === 'case_type'}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={(filterValues.case_type || []).length > 0} sx={filterLabelSx}>
                          Case type
                        </InputLabel>
                        <Select
                          multiple
                          value={filterValues.case_type || []}
                          onChange={(e) => handleFilterChange('case_type', e.target.value)}
                          disableUnderline
                          sx={{
                            fontSize: 13,
                            px: 0.5,
                            '& .MuiSelect-select': {
                              paddingRight: '28px',
                            },
                          }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => caseTypeOptions.find((o) => o.value === v)?.label)
                              .join(', ')
                          }
                        >
                          {caseTypeOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Checkbox size="small" checked={(filterValues.case_type || []).includes(o.value)} />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}

                  {columnVisibility.patient && (
                    <FilterCell widthPx={columnWidths.patient} isLast={lastVisibleKey === 'patient'}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Patient name"
                        value={filterValues.patient_full_name || ''}
                        onChange={(e) => handleFilterChange('patient_full_name', e.target.value)}
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            fontSize: 13,
                            px: 0.5,
                          },
                        }}
                        InputLabelProps={{
                          shrink: Boolean(filterValues.patient_full_name),
                          sx: filterLabelSx,
                        }}
                      />
                    </FilterCell>
                  )}

                  {columnVisibility.patient_current_state && (
                    <FilterCell
                      widthPx={columnWidths.patient_current_state}
                      isLast={lastVisibleKey === 'patient_current_state'}
                    >
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={(filterValues.case_state || []).length > 0} sx={filterLabelSx}>
                          Case state
                        </InputLabel>
                        <Select
                          multiple
                          value={filterValues.case_state || []}
                          onChange={(e) => handleFilterChange('case_state', e.target.value)}
                          disableUnderline
                          sx={{
                            fontSize: 13,
                            px: 0.5,
                            '& .MuiSelect-select': {
                              paddingRight: '28px',
                            },
                          }}
                          renderValue={(selected) =>
                            (selected as string[]).map((v) => stateOptions.find((o) => o.value === v)?.label).join(', ')
                          }
                        >
                          {stateOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Checkbox size="small" checked={(filterValues.case_state || []).includes(o.value)} />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}

                  {columnVisibility.client_tag && (
                    <FilterCell widthPx={columnWidths.client_tag} isLast={lastVisibleKey === 'client_tag'}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={((filterValues.client_tag as string[]) || []).length > 0} sx={filterLabelSx}>
                          Client tags
                        </InputLabel>
                        <Select
                          multiple
                          value={(filterValues.client_tag as string[]) || []}
                          onChange={(e) =>
                            commitFilters({
                              ...filterValues,
                              client_tag: e.target.value as string[],
                              client_division_ids: undefined,
                            })
                          }
                          disableUnderline
                          sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                          renderValue={(selected) =>
                            (selected as string[]).map((v) => clientTagOptions.find((o) => o.value === v)?.label).join(', ')
                          }
                        >
                          {clientTagOptions.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              <Checkbox
                                size="small"
                                checked={((filterValues.client_tag as string[]) || []).includes(o.value)}
                              />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}

                  {columnVisibility.client_division && (
                    <FilterCell widthPx={columnWidths.client_division} isLast={lastVisibleKey === 'client_division'}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel
                          shrink={((filterValues.client_division_ids as string[]) || []).length > 0}
                          sx={filterLabelSx}
                        >
                          {(filterValues.client_tag as string[])?.length ? 'Division' : 'Select a client tag first'}
                        </InputLabel>
                        <Select
                          multiple
                          disabled={!(filterValues.client_tag as string[])?.length}
                          value={(filterValues.client_division_ids as string[]) || []}
                          onChange={(e) => handleFilterChange('client_division_ids', e.target.value)}
                          disableUnderline
                          sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => divisionFilterOptions.find((o) => o.id === v)?.label)
                              .join(', ')
                          }
                        >
                          {divisionFilterOptions.map((o) => (
                            <MenuItem key={o.id} value={o.id}>
                              <Checkbox
                                size="small"
                                checked={((filterValues.client_division_ids as string[]) || []).includes(o.id)}
                              />
                              <ListItemText primary={o.label} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}

                  {columnVisibility.status && (
                    <FilterCell widthPx={columnWidths.status} isLast={lastVisibleKey === 'status'}>
                      <FormControl variant="standard" fullWidth>
                        <InputLabel shrink={(filterValues.status || []).length > 0} sx={filterLabelSx}>
                          Status
                        </InputLabel>
                        <Select
                          multiple
                          value={filterValues.status || []}
                          onChange={(e) => handleFilterChange('status', e.target.value)}
                          disableUnderline
                          sx={{
                            fontSize: 13,
                            px: 0.5,
                            '& .MuiSelect-select': {
                              paddingRight: '28px',
                            },
                          }}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((v) => caseStatusOptions.find((o) => o.value === v)?.label)
                              .join(', ')
                          }
                        >
                          {caseStatusOptions
                            .filter(
                              (o) => o.value !== Status.AcceptedCareAdvisor && o.value !== Status.InProgressCareAdvisor,
                            )
                            .map((o) => (
                              <MenuItem key={o.value} value={o.value}>
                                <Checkbox size="small" checked={(filterValues.status || []).includes(o.value)} />
                                <ListItemText primary={o.label} />
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </FilterCell>
                  )}

                  {columnVisibility.id && (
                    <FilterCell widthPx={columnWidths.id} isLast={lastVisibleKey === 'id'}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label="Case ID"
                        value={filterValues.case_id || ''}
                        onChange={(e) => handleFilterChange('case_id', e.target.value)}
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            fontSize: 13,
                            px: 0.5,
                          },
                        }}
                        InputLabelProps={{
                          shrink: Boolean(filterValues.case_id),
                          sx: filterLabelSx,
                        }}
                      />
                    </FilterCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {cases.length > 0 ? (
                  cases.map((row) => (
                    <TableRow key={row.id} hover onClick={() => handleRowClick(row.id)} sx={{ cursor: 'pointer' }}>
                      {columnVisibility.scheduled_at && (
                        <TableCell sx={bodyCellSx}>
                          {calculateCaseScheduled(row.type, row.video_conference_date || '')}
                        </TableCell>
                      )}

                      {columnVisibility.due_date && (
                        <TableCell sx={bodyCellSx}>
                          {calculateProviderCaseDue(
                            row.type,
                            row.started_at,
                            row.video_conference_date || '',
                            row.due_date,
                          )}
                        </TableCell>
                      )}

                      {columnVisibility.case_type && <TableCell sx={bodyCellSx}>{getFriendlyName(row.type)}</TableCell>}

                      {columnVisibility.patient && (
                        <TableCell sx={bodyCellSx}>{row.patient?.full_name ?? '-'}</TableCell>
                      )}

                      {columnVisibility.patient_current_state && (
                        <TableCell sx={bodyCellSx}>{row.patient_current_state}</TableCell>
                      )}

                      {columnVisibility.client_tag && <TableCell sx={bodyCellSx}>{getClientTagStr(row)}</TableCell>}

                      {columnVisibility.client_division && (
                        <TableCell sx={bodyCellSx}>{getClientDivisionStr(row)}</TableCell>
                      )}

                      {columnVisibility.status && (
                        <TableCell sx={bodyCellSx}>
                          {(() => {
                            const statusText = getTextForStatus(userInfo.role.name, row.type, row.status);
                            const statusStyle = getStatusColorStyles(row.status);

                            return (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: '999px',
                                  fontSize: 12,
                                  fontWeight: 500,
                                  lineHeight: 1.2,
                                  color: statusStyle.color,
                                  backgroundColor: statusStyle.backgroundColor,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {statusText}
                              </Box>
                            );
                          })()}
                        </TableCell>
                      )}

                      {columnVisibility.id && <TableCell sx={bodyCellSx}>{row.id}</TableCell>}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      sx={{
                        ...bodyCellSx,
                        textAlign: 'center',
                        color: '#6B7280',
                        py: 3,
                      }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <div
            ref={customScrollbarRef}
            className={styles.customHorizontalScrollbar}
            onMouseDown={handleScrollbarTrackClick}
            style={{
              opacity: showCustomScrollbar ? 1 : 0,
              pointerEvents: showCustomScrollbar ? 'auto' : 'none',
            }}
          >
            <div
              className={styles.customHorizontalScrollbarThumb}
              onMouseDown={handleScrollbarThumbMouseDown}
              style={{
                width: `${scrollbarThumbWidth}px`,
                transform: `translateX(${scrollbarThumbLeft}px)`,
              }}
            />
          </div>

          <TablePagination
            component="div"
            count={totalNewCases}
            page={pageIndex}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 100]}
            className={styles.tablePagination}
            onPageChange={(_, nextPage) => {
              if (nextPage > pageIndex) {
                loadNextPage();
              } else if (nextPage < pageIndex) {
                loadPreviousPage();
              }
              setTargetPageIndex(nextPage);
              writeSessionTableState(SESSION_STATE_KEY, {
                ...filterValues,
                page: nextPage + 1,
                rowsPerPage,
                ...(sortBy ? { sort_by: sortBy } : {}),
                ...(sortDirection ? { sort_direction: sortDirection } : {}),
              });
            }}
            onRowsPerPageChange={(e) => {
              const nextRowsPerPage = +e.target.value;
              setRowsPerPage(nextRowsPerPage);
              setTargetPageIndex(0);
              writeSessionTableState(SESSION_STATE_KEY, {
                ...filterValues,
                page: 1,
                rowsPerPage: nextRowsPerPage,
                ...(sortBy ? { sort_by: sortBy } : {}),
                ...(sortDirection ? { sort_direction: sortDirection } : {}),
              });
            }}
          />
        </Paper>
      </Box>

      {dataLoading && cases.length > 0 && <Loading dotted />}
    </div>
  );
};

export default Appointments;
